'use client'

import { useEffect } from 'react'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { ChartContainer, BarChartComponent, PieChartComponent } from "@/components/ui/charts"
import { motion, useAnimation } from 'framer-motion'
import { useInView } from 'react-intersection-observer'
import { AnalyticsSection } from '@/components/analytics-section'
import { BarChart, FileText, TrendingUp, Download } from 'lucide-react'

export default function AdvancedAnalyticsPage() {
  const controls = useAnimation()
  const [ref, inView] = useInView({
    triggerOnce: true,
    threshold: 0.1,
  })

  useEffect(() => {
    if (inView) {
      controls.start('visible')
    }
  }, [controls, inView])

  const sectionVariants = {
    hidden: { opacity: 0, y: 50 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.5 } },
  }

  const features = [
    {
      icon: BarChart,
      title: 'Comprehensive Analytics',
      description: 'Deep insights into contract trends, win rates, and market dynamics',
    },
    {
      icon: FileText,
      title: 'Custom Reports',
      description: 'Generate tailored reports based on your specific requirements',
    },
    {
      icon: TrendingUp,
      title: 'Performance Tracking',
      description: 'Monitor your success metrics and identify improvement areas',
    },
    {
      icon: Download,
      title: 'Data Export',
      description: 'Export analytics in multiple formats for further analysis',
    },
  ]

  return (
    <div className="container max-w-7xl mx-auto px-4 py-16">
      {/* Hero Section */}
      <motion.div
        ref={ref}
        variants={sectionVariants}
        initial="hidden"
        animate={controls}
        className="text-center mb-16"
      >
        <h1 className="text-5xl font-bold mb-6">Advanced Analytics</h1>
        <p className="text-xl text-muted-foreground max-w-3xl mx-auto">
          Dive deep into government contracting data with our comprehensive analytics dashboard
        </p>
      </motion.div>

      {/* Features Grid */}
      <motion.div
        variants={sectionVariants}
        initial="hidden"
        animate={controls}
        className="grid md:grid-cols-2 lg:grid-cols-4 gap-8 mb-16"
      >
        {features.map((feature) => (
          <Card key={feature.title} className="bg-card">
            <CardHeader>
              <div className="flex items-center gap-3 mb-4">
                <div className="p-2 rounded-lg bg-primary/10">
                  <feature.icon className="h-6 w-6 text-primary" />
                </div>
                <CardTitle className="text-xl">{feature.title}</CardTitle>
              </div>
            </CardHeader>
            <CardContent>
              <p className="text-muted-foreground">{feature.description}</p>
            </CardContent>
          </Card>
        ))}
      </motion.div>

      {/* Analytics Dashboard */}
      <AnalyticsSection />

      {/* Additional Features */}
      <motion.div
        variants={sectionVariants}
        initial="hidden"
        animate={controls}
        className="mt-16 grid gap-6 md:grid-cols-2"
      >
        <Card className="bg-card">
          <CardHeader>
            <CardTitle className="text-2xl text-primary">Custom Reports</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-muted-foreground mb-4">
              Create customized reports based on your specific needs:
            </p>
            <ul className="list-disc pl-5 space-y-2 text-muted-foreground">
              <li>Contract performance metrics</li>
              <li>Competitor analysis</li>
              <li>Market trends and forecasts</li>
              <li>Opportunity pipeline tracking</li>
            </ul>
          </CardContent>
        </Card>

        <Card className="bg-card">
          <CardHeader>
            <CardTitle className="text-2xl text-primary">Data Export</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-muted-foreground mb-4">
              Export your analytics data in various formats:
            </p>
            <ul className="list-disc pl-5 space-y-2 text-muted-foreground">
              <li>CSV for spreadsheet analysis</li>
              <li>PDF reports for presentations</li>
              <li>API access for integration</li>
              <li>Real-time data feeds</li>
            </ul>
          </CardContent>
        </Card>
      </motion.div>
    </div>
  )
} 